/**
 * COMPTABILITÉ — SUIVI DU CHIFFRE D'AFFAIRES.
 *
 * ═══ CE QUE CET ÉCRAN EST, ET CE QU'IL N'EST PAS ═══
 *
 * C'est un READ MODEL. Le chiffre d'affaires n'a que deux points d'entrée :
 * une clôture FINALISÉE écrit des composantes de recette ; une nuit VALIDÉE
 * SANS CLÔTURE DÉTAILLÉE arrête un chiffre en bloc, sans composantes. Rien ne
 * se saisit ici, et rien ne s'y corrige : une correction est un AJUSTEMENT
 * motivé, créé depuis le CA annuel, qui laisse chaque ligne visible.
 *
 * ═══ LES NUITS VALIDÉES : COMPTÉES, JAMAIS MÊLÉES ═══
 *
 * Le serveur les sert à part (`validated_nights`, à côté de `nights`), et
 * l'écran garde ce rangement jusqu'au bout :
 *
 *   · au niveau du mois, de l'exercice et de l'année, une ligne « Validé sans
 *     clôture détaillée » s'intercale entre Calculé et Ajustements — on refait
 *     l'addition à l'écran : Calculé + Validé + Ajustements = Retenu ;
 *   · dans les vues de nuits, elles ont LEUR bloc, sous les clôtures, avec leur
 *     pastille « Validée sans clôture », leur total, et un lien vers leur fiche
 *     (`/comptabilite/nuits/<date>`). Les glisser parmi les clôtures leur
 *     aurait prêté une révision et une ventilation qu'elles n'ont pas ;
 *   · ouverte en vue Jour, une nuit validée se DIT validée — et non « aucune
 *     recette », qui nierait un chiffre que le retenu du mois compte ;
 *   · un mois peut n'avoir QUE des nuits validées : il s'affiche, et ses nuits
 *     se comptent « 3 nuits validées sans clôture », jamais « 0 nuit ».
 *
 * Un montant non servi s'écrit « — », jamais « 0,00 € », et un total qui en
 * dépend devient « — » à son tour (`totalStrict`).
 *
 * ═══ QUATRE PROFONDEURS, UNE SEULE DONNÉE ═══
 *
 *     saison  →  mois  →  nuit  →  point de vente
 *
 * Le fil de profondeur remonte d'un cran sans quitter l'écran, et la vue
 * choisie vit dans l'URL : recharger la page ou partager le lien retombe sur
 * la même lecture. Le tableur dense (« matrice ») existe, mais en SECOND :
 * une matrice de douze colonnes est une vue d'expert, pas une page d'accueil.
 *
 * ═══ TROIS PIÈGES ÉVITÉS ICI, ET COMMENT ═══
 *
 *  · LE ZÉRO QUI N'EN EST PAS. Une donnée absente rend « — », jamais
 *    « 0,00 € » : ne pas savoir et valoir zéro sont deux choses différentes.
 *  · LE CUMUL RECALCULÉ. Les nuits affichées se totalisent (« total des nuits
 *    affichées »), mais le CUMUL DU MOIS et celui de l'EXERCICE viennent du
 *    serveur ou ne viennent pas. Additionner des nuits pour en faire un mois
 *    ignorerait les ajustements de niveau mois — un chiffre faux, d'apparence
 *    juste.
 *  · L'ANNÉE CIVILE PRISE POUR L'EXERCICE. L'année civile vient de
 *    `/annual?year`, l'exercice de `/season?date` — l'exercice qui CONTIENT
 *    la date consultée. (16/10/2026) L'écran lisait l'exercice « ouvert
 *    pendant l'année » de la date : le 15/03/2026 montrait 2026-27, et le
 *    15/03/2025 un 2025-26 réduit à septembre → décembre. Une année ne dit pas
 *    l'exercice ; la date, rapportée au jour d'ouverture réglé, le dit.
 *
 * ROUTES SERVIES (vérifiées dans `apps/api/src/routes/finance-revenue.ts`) :
 *   GET /api/v1/finance/revenue/nights?from&to   finance.revenue.read
 *   GET /api/v1/finance/revenue/annual?year      finance.revenue.read
 *   GET /api/v1/finance/revenue/season?date      finance.revenue.read
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { CalendarRange, ChevronLeft, ChevronRight, ExternalLink, Info, Store, TrendingUp } from 'lucide-react'
import { Link } from '../router.js'
import { composerDeclencheurs } from '../shell/menu-contextuel.js'
import { nuitDuChiffreAffaires, useMenuSoiree } from '../soirees/menu-soiree.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { DatePicker, SegmentedControl } from '../admin/pickers.js'
import { formatMoney } from './money.js'
import { remplacerRecherche } from './adresse-ecran.js'
import { moisValide, ymdValide } from './date-adresse.js'
import {
  Comparaison, Ecart, EtatBloc, LIBELLE_VALIDE, PastilleCloture, PastilleValidee, TroisLignes,
  aDesValidees, ajouterJours, anneeDe, colonnesActives, debutSemaine, dernierJour, libelleJour,
  libelleJourLong, libelleMois, libelleMoisCourt, libelleValidation, lireAnnuel, lirePeriodeNuits, lireSaison, moisDe,
  moisSuivant, montantDe, nuitsTexte, premierJour, totalChamp, totalColonne, totalStrict, useRessource,
  ymdAujourdhui, exerciceVoisin,
  type Colonne, type SaisonCA, type MoisCA, type NuitCA, type NuitValideeCA, type RefMois,
} from './RevenueShared.js'
import './finance.css'
import './finance-revenue.css'

type Vue = 'jour' | 'semaine' | 'mois' | 'saison'
type Rendu = 'synthese' | 'matrice'

const VUES: { value: Vue; label: string }[] = [
  { value: 'jour', label: 'Jour' },
  { value: 'semaine', label: 'Semaine' },
  { value: 'mois', label: 'Mois' },
  { value: 'saison', label: 'Saison' },
]

/** La période couverte par une vue, en nuits commerciales incluses. */
function bornes(vue: Vue, jour: string): { de: string; a: string } {
  if (vue === 'jour') return { de: jour, a: jour }
  if (vue === 'semaine') {
    const lundi = debutSemaine(jour)
    return { de: lundi, a: ajouterJours(lundi, 6) }
  }
  const ym = moisDe(jour)
  return { de: premierJour(ym), a: dernierJour(ym) }
}

function pas(vue: Vue, jour: string, sens: 1 | -1): string {
  if (vue === 'jour') return ajouterJours(jour, sens)
  if (vue === 'semaine') return ajouterJours(jour, 7 * sens)
  if (vue === 'mois') return `${moisSuivant(moisDe(jour), sens)}-15`
  return exerciceVoisin(jour, sens)
}

function libellePeriode(vue: Vue, jour: string, exercice: SaisonCA | null): string {
  if (vue === 'jour') return libelleJourLong(jour)
  if (vue === 'semaine') {
    const { de, a } = bornes(vue, jour)
    return `${libelleJour(de)} – ${libelleJour(a)}`
  }
  if (vue === 'mois') return libelleMois(moisDe(jour))
  if (exercice?.saison) return `${exercice.saison.debut.slice(0, 4)} – ${exercice.saison.fin.slice(0, 4)}`
  return String(anneeDe(moisDe(jour)))
}

/* ═════════════════════════════ tables ═════════════════════════════ */

/**
 * La clôture d'une nuit. Le read model ne rend pas de statut — et pour cause :
 * SEULES les clôtures finalisées l'alimentent. On affiche donc le lien et la
 * révision, pas une pastille d'état déduite ; si un statut arrive un jour, il
 * s'affiche tel quel.
 */
function CelluleCloture({ nuit }: { nuit: NuitCA }) {
  if (nuit.statut !== null) return <PastilleCloture statut={nuit.statut} />
  if (nuit.closingId === null) return <span className="fr-mini">—</span>
  return (
    <Link
      className="lc-link" href={`/comptabilite/clotures/${nuit.closingId}`}
      onClick={(e) => e.stopPropagation()}
    >
      {nuit.revision !== null ? `Révision ${nuit.revision}` : 'Ouvrir'} <ExternalLink size={13} aria-hidden="true" />
    </Link>
  )
}

/**
 * Vue de synthèse d'une période de nuits. Les colonnes « Soirée » et « Écart »
 * n'apparaissent que si au moins une nuit les porte : le read model ne les
 * transmet pas toujours, et une colonne de tirets n'apprend rien.
 */
function TableNuitsSynthese(props: { nuits: NuitCA[]; onOuvrir: (date: string) => void }) {
  const menuSoiree = useMenuSoiree()
  const colSoirees = props.nuits.some((n) => n.soirees.length > 0)
  const colEcart = props.nuits.some((n) => n.ecart !== null)
  return (
    <div className="fi-tablewrap">
      <table className="fi-table">
        <thead>
          <tr>
            <th scope="col">Nuit</th>
            {colSoirees ? <th scope="col">Soirée</th> : null}
            <th scope="col">Catégories actives</th>
            <th scope="col" className="fi-money">Retenu</th>
            {colEcart ? <th scope="col" className="fi-money">Écart</th> : null}
            <th scope="col">Clôture</th>
          </tr>
        </thead>
        <tbody>
          {props.nuits.map((n) => (
            <tr
              key={n.date} className="fr-row" tabIndex={0} role="button"
              aria-label={`Détail de la nuit du ${libelleJourLong(n.date)}`}
              onClick={() => props.onOuvrir(n.date)}
              // L'Entrée de la ligne et le menu clic droit sur le même élément : composés, aucun n'écrase l'autre.
              {...composerDeclencheurs(menuSoiree.nuit(nuitDuChiffreAffaires(n)), {
                onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onOuvrir(n.date) } },
              })}
            >
              <td className="fr-cell-main">{libelleJour(n.date)}</td>
              {colSoirees ? <td>{n.soirees.length > 0 ? n.soirees.join(' · ') : <span className="fr-mini">—</span>}</td> : null}
              <td>
                {n.composantes.length > 0
                  ? <span className="fr-mini">{n.composantes.map((c) => c.libelle).join(' · ')}</span>
                  : <span className="fr-mini">—</span>}
              </td>
              <td className="fi-money fi-money--fort">{formatMoney(n.retenu)}</td>
              {colEcart ? <td className="fi-money"><Ecart cents={n.ecart} /></td> : null}
              <td><CelluleCloture nuit={n} /></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={colSoirees ? 3 : 2}>
              Total des {props.nuits.length} nuit{props.nuits.length > 1 ? 's' : ''} clôturée{props.nuits.length > 1 ? 's' : ''} affichée{props.nuits.length > 1 ? 's' : ''}
            </td>
            <td className="fi-money">{formatMoney(totalChamp(props.nuits, (n) => n.retenu))}</td>
            {colEcart ? <td className="fi-money"><Ecart cents={totalChamp(props.nuits, (n) => n.ecart)} /></td> : null}
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** Vue matrice : dense, une colonne par composante RÉELLEMENT alimentée. */
function TableMatriceNuits(props: { nuits: NuitCA[]; colonnes: Colonne[]; onOuvrir: (date: string) => void }) {
  const menuSoiree = useMenuSoiree()
  return (
    <div className="fi-tablewrap">
      <table className="fi-table">
        <thead>
          <tr>
            <th scope="col">Nuit</th>
            {props.colonnes.map((c) => <th key={c.code} scope="col" className="fi-money">{c.libelle}</th>)}
            <th scope="col" className="fi-money">Retenu</th>
          </tr>
        </thead>
        <tbody>
          {props.nuits.map((n) => (
            <tr
              key={n.date} className="fr-row" tabIndex={0} role="button"
              aria-label={`Détail de la nuit du ${libelleJourLong(n.date)}`}
              onClick={() => props.onOuvrir(n.date)}
              // L'Entrée de la ligne et le menu clic droit sur le même élément : composés, aucun n'écrase l'autre.
              {...composerDeclencheurs(menuSoiree.nuit(nuitDuChiffreAffaires(n)), {
                onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onOuvrir(n.date) } },
              })}
            >
              <td className="fr-cell-main">
                {libelleJour(n.date)}
                {n.soirees.length > 0 ? <small className="fr-cell-sub">{n.soirees.join(' · ')}</small> : null}
              </td>
              {props.colonnes.map((c) => (
                <td key={c.code} className="fi-money">{formatMoney(montantDe(n.composantes, c.code))}</td>
              ))}
              <td className="fi-money fi-money--fort">{formatMoney(n.retenu)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Total des clôtures affichées</td>
            {props.colonnes.map((c) => (
              <td key={c.code} className="fi-money">{formatMoney(totalColonne(props.nuits, c.code))}</td>
            ))}
            <td className="fi-money">{formatMoney(totalChamp(props.nuits, (n) => n.retenu))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/**
 * Les mois d'un exercice — la descente commence ici.
 *
 * En synthèse, chaque ligne se lit comme une addition : Calculé + Validé sans
 * clôture détaillée + Ajustements = Retenu. La colonne « Validé » est un TERME
 * de cette addition : elle est là dès que le serveur la sert, y compris à
 * « 0,00 € » (un zéro mesuré — aucune nuit validée ce mois-là). En matrice,
 * les colonnes ventilent le CALCULÉ par catégorie ; une nuit validée n'a pas
 * de catégorie, sa colonne ne s'ajoute donc que si une nuit validée existe sur
 * la période — sinon ce serait une colonne de zéros.
 */
function TableMois(props: {
  mois: MoisCA[]
  colonnes: Colonne[]
  rendu: Rendu
  precedent: RefMois[]
  onOuvrir: (ym: string) => void
}) {
  const refDe = (ym: string): RefMois | undefined =>
    props.precedent.find((m) => m.mois.slice(5) === ym.slice(5))
  // Une colonne que personne n'alimente ne s'affiche pas.
  const colonneNuits = props.mois.some((m) => m.nuits !== null || m.nuitsValidees !== null)
  const colonneValide = props.rendu === 'matrice'
    ? aDesValidees(props.mois)
    : props.mois.some((m) => m.valide !== null)
  const thValide = colonneValide ? <th scope="col" className="fi-money fr-th-long">{LIBELLE_VALIDE}</th> : null
  return (
    <div className="fi-tablewrap">
      <table className="fi-table">
        <thead>
          <tr>
            <th scope="col">Mois</th>
            {props.rendu === 'matrice'
              ? <>{props.colonnes.map((c) => <th key={c.code} scope="col" className="fi-money">{c.libelle}</th>)}{thValide}</>
              : <>{colonneNuits ? <th scope="col">Nuits</th> : null}<th scope="col" className="fi-money">Calculé</th>{thValide}<th scope="col" className="fi-money">Ajustements</th></>}
            <th scope="col" className="fi-money">Retenu</th>
            {props.precedent.length > 0 ? <th scope="col">Année précédente</th> : null}
          </tr>
        </thead>
        <tbody>
          {props.mois.map((m) => (
            <tr
              key={m.mois} className="fr-row" tabIndex={0} role="button"
              aria-label={`Détail de ${libelleMois(m.mois)}`}
              onClick={() => props.onOuvrir(m.mois)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onOuvrir(m.mois) } }}
            >
              <td className="fr-cell-main">{libelleMois(m.mois)}</td>
              {props.rendu === 'matrice'
                ? (
                  <>
                    {props.colonnes.map((c) => (
                      <td key={c.code} className="fi-money">{formatMoney(montantDe(m.composantes, c.code))}</td>
                    ))}
                    {colonneValide ? <td className="fi-money">{formatMoney(m.valide)}</td> : null}
                  </>
                )
                : (
                  <>
                    {colonneNuits ? <td>{nuitsTexte(m.nuits, m.nuitsValidees) ?? <span className="fr-mini">—</span>}</td> : null}
                    <td className="fi-money fi-money--faible">{formatMoney(m.calcule)}</td>
                    {colonneValide ? <td className="fi-money">{formatMoney(m.valide)}</td> : null}
                    <td className="fi-money">{formatMoney(m.ajustements, { signe: true })}</td>
                  </>
                )}
              <td className="fi-money fi-money--fort">{formatMoney(m.retenu)}</td>
              {props.precedent.length > 0 ? (
                <td><Comparaison actuel={m.retenu} reference={refDe(m.mois)?.retenu ?? null} /></td>
              ) : null}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Total des mois affichés</td>
            {props.rendu === 'matrice'
              ? (
                <>
                  {props.colonnes.map((c) => (
                    <td key={c.code} className="fi-money">{formatMoney(totalColonne(props.mois, c.code))}</td>
                  ))}
                  {colonneValide ? <td className="fi-money">{formatMoney(totalChamp(props.mois, (m) => m.valide))}</td> : null}
                </>
              )
              : (
                <>
                  {colonneNuits ? <td /> : null}
                  <td className="fi-money">{formatMoney(totalChamp(props.mois, (m) => m.calcule))}</td>
                  {colonneValide ? <td className="fi-money">{formatMoney(totalChamp(props.mois, (m) => m.valide))}</td> : null}
                  <td className="fi-money">{formatMoney(totalChamp(props.mois, (m) => m.ajustements), { signe: true })}</td>
                </>
              )}
            <td className="fi-money">{formatMoney(totalChamp(props.mois, (m) => m.retenu))}</td>
            {props.precedent.length > 0 ? <td /> : null}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/* ═══════════════ les nuits validées sans clôture détaillée ═══════════════ */

/**
 * LES NUITS VALIDÉES SANS CLÔTURE DÉTAILLÉE DE LA PÉRIODE — dans leur propre
 * bloc, sous les clôtures, avec leur propre total.
 *
 * Jamais mêlées aux nuits clôturées : elles n'ont ni composantes, ni point de
 * vente, ni révision. Rangées dans la même table, elles auraient eu l'air de
 * clôtures sans détail, et le total des clôtures aurait compté un chiffre
 * qu'aucune clôture n'a arrêté. La ligne ouvre la nuit en vue Jour, comme
 * celle d'une clôture ; le lien mène à sa FICHE (`/comptabilite/nuits/<date>`),
 * là où la validation se lit en entier — proposition de Gestion, motif — et
 * s'annule.
 *
 * Le total est STRICT : un seul montant non servi, et il s'écrit « — » plutôt
 * que d'additionner les seules lignes lisibles.
 */
function TableNuitsValidees(props: { validees: NuitValideeCA[]; onOuvrir: (date: string) => void }) {
  const menuSoiree = useMenuSoiree()
  const n = props.validees.length
  const s = n > 1 ? 's' : ''
  // Les entrées sont servies avec la validation ; une colonne de tirets
  // n'apprendrait rien, elle ne s'affiche que si une nuit en porte.
  const colEntrees = props.validees.some((v) => v.entrees !== null)
  return (
    <section aria-label="Nuits validées sans clôture détaillée">
      <div className="fr-section__head">
        <h2 className="fr-section__title">Nuits validées sans clôture détaillée</h2>
        <span className="fr-section__hint">
          Un chiffre arrêté en bloc, sans composantes : compté dans le retenu du mois, à part du calculé.
        </span>
      </div>
      <div className="fi-tablewrap">
        <table className="fi-table">
          <thead>
            <tr>
              <th scope="col">Nuit</th>
              <th scope="col">Validation</th>
              {colEntrees ? <th scope="col" className="fi-money">Entrées</th> : null}
              <th scope="col" className="fi-money fr-th-long">{LIBELLE_VALIDE}</th>
              <th scope="col">Fiche</th>
            </tr>
          </thead>
          <tbody>
            {props.validees.map((v) => (
              <tr
                key={v.date} className="fr-row" tabIndex={0} role="button"
                aria-label={`Détail de la nuit ${v.reprise ? 'reprise de l’historique' : 'validée sans clôture'} du ${libelleJourLong(v.date)}`}
                onClick={() => props.onOuvrir(v.date)}
                // Une nuit validée sans clôture détaillée : pas de clôture à ouvrir.
                {...composerDeclencheurs(menuSoiree.nuit({ date: v.date, cloture: null }), {
                  onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onOuvrir(v.date) } },
                })}
              >
                <td className="fr-cell-main">{libelleJour(v.date)}</td>
                <td>
                  <PastilleValidee reprise={v.reprise} />
                  <span className="fr-validees__qui">{libelleValidation(v)}</span>
                </td>
                {colEntrees ? <td className="fi-money">{v.entrees ?? '—'}</td> : null}
                <td className="fi-money fi-money--fort">{formatMoney(v.cents)}</td>
                <td>
                  <Link
                    className="lc-link" href={`/comptabilite/nuits/${v.date}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Fiche de la nuit <ExternalLink size={13} aria-hidden="true" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={colEntrees ? 3 : 2}>Total des {n} nuit{s} validée{s} affichée{s}</td>
              <td className="fi-money">{formatMoney(totalStrict(props.validees, (v) => v.cents))}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}

/**
 * LE TOTAL D'UNE PÉRIODE QUI A LES DEUX FAMILLES : chacune sa ligne, puis la
 * somme — l'addition se refait à l'écran.
 *
 * C'est le total des NUITS AFFICHÉES, pas le cumul du mois : un ajustement
 * porte sur un mois ou une année, jamais sur une nuit, et le cumul du mois —
 * qui l'inclut — vient du serveur (« Cumul du mois »), jamais d'ici.
 */
function TotalNuitsAffichees(props: { clotures: number | null; validees: number | null }) {
  const total = props.clotures === null || props.validees === null ? null : props.clotures + props.validees
  return (
    <div className="fr-triline">
      <span className="fr-triline__label">Nuits clôturées affichées</span>
      <span className="fi-money">{formatMoney(props.clotures)}</span>
      <span className="fr-triline__label">{LIBELLE_VALIDE}</span>
      <span className="fi-money">{formatMoney(props.validees)}</span>
      <span className="fr-triline__sep" />
      <span className="fr-triline__label fr-triline__label--fort">Total des nuits affichées</span>
      <span className="fi-money fi-money--fort">{formatMoney(total)}</span>
      <span className="fr-triline__note">
        Sans les ajustements, qui portent sur un mois ou une année et jamais sur une
        nuit : le cumul du mois, qui les compte, est lu plus haut.
      </span>
    </div>
  )
}

/**
 * UNE NUIT VALIDÉE SANS CLÔTURE DÉTAILLÉE, ouverte en vue Jour.
 *
 * Ni composantes, ni point de vente, ni clôture : la vue le DIT, au lieu
 * d'afficher « Aucune recette sur cette période » — qui nierait un chiffre que
 * le retenu du mois compte bel et bien. Le détail de la validation vit sur la
 * fiche de la nuit, vers laquelle elle renvoie.
 */
function VueNuitValidee({ nuit }: { nuit: NuitValideeCA }) {
  return (
    <div className="fr-stack">
      <div className="fr-kpis">
        <div className="fr-kpi fr-kpi--accent">
          <span className="fi-kpi__label">{nuit.reprise ? 'Repris de l’historique (Total DAF)' : LIBELLE_VALIDE}</span>
          <span className="fi-kpi__value">{formatMoney(nuit.cents)}</span>
          <span className="fr-kpi__foot">Compté dans le retenu du mois, nommé à part du calculé.</span>
        </div>
        {nuit.entrees !== null ? (
          <div className="fr-kpi">
            <span className="fi-kpi__label">Entrées arrêtées</span>
            <span className="fi-kpi__value">{nuit.entrees}</span>
            <span className="fr-kpi__foot">Le nombre d’entrées arrêté avec le chiffre.</span>
          </div>
        ) : null}
        <div className="fr-kpi">
          <span className="fi-kpi__label">{nuit.reprise ? 'Provenance' : 'Validation'}</span>
          <span className="fr-inline"><PastilleValidee reprise={nuit.reprise} /></span>
          <span className="fr-kpi__foot">
            {libelleValidation(nuit)}
            {/* Le motif d'une reprise redit la provenance que le libellé vient de dire. */}
            {nuit.motif !== null && !nuit.reprise ? <> — « {nuit.motif} »</> : null}
          </span>
        </div>
      </div>
      <div className="fr-note">
        <Info size={16} aria-hidden="true" />
        <span>
          <strong>Aucune clôture détaillée.</strong> Cette nuit a été validée en bloc :
          elle n’a ni composantes, ni point de vente, ni révision. La proposition de
          Gestion et le détail de la validation se lisent sur la
          {' '}<Link className="lc-link" href={`/comptabilite/nuits/${nuit.date}`}>fiche de la nuit</Link>.
        </span>
      </div>
    </div>
  )
}

/* ═════════════════════════ la nuit, puis le point de vente ═══════════════ */

function VueNuit(props: { nuit: NuitCA; pdv: string | null; onPdv: (id: string | null) => void }) {
  const { nuit } = props
  const choisi = nuit.pointsDeVente.find((p) => p.id === props.pdv) ?? null
  const troisLignes = nuit.calcule !== null || nuit.ajustements !== null

  return (
    <div className="fr-stack">
      <div className="fr-kpis">
        <div className="fr-kpi fr-kpi--accent">
          <span className="fi-kpi__label">Retenu sur la nuit</span>
          <span className="fi-kpi__value">{formatMoney(nuit.retenu)}</span>
          <span className="fr-kpi__foot">
            {nuit.soirees.length > 0 ? nuit.soirees.join(' · ') : 'Issu des clôtures finalisées de cette nuit.'}
          </span>
        </div>
        {nuit.ecart !== null ? (
          <div className="fr-kpi">
            <span className="fi-kpi__label">Écart constaté</span>
            <span className="fi-kpi__value"><Ecart cents={nuit.ecart} /></span>
            <span className="fr-kpi__foot">Différence entre ce qui a été compté et ce qui a été retenu.</span>
          </div>
        ) : null}
        <div className="fr-kpi">
          <span className="fi-kpi__label">Clôture</span>
          <span className="fr-inline"><CelluleCloture nuit={nuit} /></span>
          <span className="fr-kpi__foot">
            Seules les clôtures finalisées alimentent le chiffre d’affaires : un
            brouillon en cours n’apparaît nulle part ici.
          </span>
        </div>
      </div>

      {troisLignes ? (
        <TroisLignes
          calcule={nuit.calcule} ajustements={nuit.ajustements} retenu={nuit.retenu}
          note="Un total retenu qui diffère du calcul porte toujours un ajustement motivé : il n’existe pas de correction silencieuse."
        />
      ) : (
        <p className="fr-mini">
          Une nuit n’a ni « calculé » ni « ajustements » qui lui soient propres : un
          ajustement porte sur un mois ou une année, jamais sur une nuit. Les trois
          lignes se lisent donc sur le CA annuel.
        </p>
      )}

      <section className="fr-section">
        <div className="fr-section__head">
          <h2 className="fr-section__title">Composantes de la nuit</h2>
        </div>
        {nuit.composantes.length === 0 ? (
          <div className="fi-empty">
            <div className="fi-empty__title">Aucune composante sur cette nuit</div>
            Aucune clôture finalisée n’a encore produit de recette pour cette date.
          </div>
        ) : (
          <div className="fi-tablewrap fr-compact">
            <table className="fi-table">
              <thead>
                <tr><th scope="col">Composante</th><th scope="col" className="fi-money">Montant</th></tr>
              </thead>
              <tbody>
                {nuit.composantes.map((c) => (
                  <tr key={c.code}>
                    <td className="fr-cell-main">{c.libelle}</td>
                    <td className="fi-money">{formatMoney(c.cents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total de la nuit</td>
                  <td className="fi-money">{formatMoney(nuit.retenu)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      <section className="fr-section">
        <div className="fr-section__head">
          <h2 className="fr-section__title">Par point de vente</h2>
          {choisi !== null ? (
            <button type="button" className="lc-btn lc-btn--ghost" onClick={() => props.onPdv(null)}>
              Revenir à tous les points de vente
            </button>
          ) : null}
        </div>
        {nuit.pointsDeVente.length === 0 ? (
          <p className="fr-mini">
            Aucune composante de cette nuit ne porte de point de vente. Rien n’est
            réparti au prorata : un comptoir n’affiche que ce qui lui a été
            explicitement rattaché.
          </p>
        ) : choisi !== null ? (
          <div className="fr-stack">
            <div className="fr-triline">
              <span className="fr-triline__label fr-triline__label--fort">{choisi.nom}</span>
              <span className="fi-money fi-money--fort">{formatMoney(choisi.retenu)}</span>
              <span className="fr-triline__note">
                Un point de vente ne porte que ce qui lui a été explicitement rattaché :
                le reste de la nuit vit ailleurs, il n’est pas réparti au prorata.
              </span>
            </div>
            <div className="fi-tablewrap fr-compact">
              <table className="fi-table">
                <thead><tr><th scope="col">Composante</th><th scope="col" className="fi-money">Montant</th></tr></thead>
                <tbody>
                  {choisi.composantes.map((c) => (
                    <tr key={c.code}>
                      <td className="fr-cell-main">{c.libelle}</td>
                      <td className="fi-money">{formatMoney(c.cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="fi-tablewrap fr-compact">
            <table className="fi-table">
              <thead><tr><th scope="col">Point de vente</th><th scope="col" className="fi-money">Retenu</th></tr></thead>
              <tbody>
                {nuit.pointsDeVente.map((p) => (
                  <tr
                    key={p.id} className="fr-row" tabIndex={0} role="button"
                    aria-label={`Détail du point de vente ${p.nom}`}
                    onClick={() => props.onPdv(p.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onPdv(p.id) } }}
                  >
                    <td className="fr-cell-main"><Store size={14} aria-hidden="true" /> {p.nom}</td>
                    <td className="fi-money">{formatMoney(p.retenu)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total rattaché à un point de vente</td>
                  <td className="fi-money">{formatMoney(totalChamp(nuit.pointsDeVente, (p) => p.retenu))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

/* ═════════════════════════════ la page ═════════════════════════════ */

function etatInitial(): { vue: Vue; jour: string; rendu: Rendu; pdv: string | null } {
  const q = new URLSearchParams(window.location.search)
  const v = q.get('vue')
  const d = q.get('date')
  const m = q.get('mois')
  const vue: Vue = v === 'jour' || v === 'semaine' || v === 'mois' || v === 'saison' ? v : 'mois'
  // La forme du tableau et le point de vente ouvert voyagent AUSSI dans
  // l'adresse : un Retour depuis une clôture retrouvait sinon la synthèse,
  // le point de vente refermé.
  const rendu: Rendu = q.get('rendu') === 'matrice' ? 'matrice' : 'synthese'
  const brutPdv = q.get('pdv')
  const pdv = vue === 'jour' && brutPdv !== null && /^[0-9a-z-]{8,64}$/i.test(brutPdv) ? brutPdv : null
  // Une VRAIE date, pas seulement sa forme : « 2026-02-30 » partait au
  // serveur, et la vue Saison affichait « Saison à configurer » sur un
  // exercice réglé. Une date impossible retombe sur aujourd'hui.
  if (ymdValide(d)) return { vue, jour: d, rendu, pdv }
  // `?mois=YYYY-MM` : la descente venue du CA annuel. On se place au 15 —
  // n'importe quel jour du mois désigne le même mois, et le 15 ne bascule
  // jamais dans un mois voisin quelle que soit la longueur du mois.
  if (moisValide(m)) return { vue: v === null ? 'mois' : vue, jour: `${m}-15`, rendu, pdv: null }
  return { vue, jour: ymdAujourdhui(), rendu, pdv: null }
}

export function FinanceRevenuePage() {
  const initial = useMemo(etatInitial, [])
  const [vue, setVue] = useState<Vue>(initial.vue)
  const [jour, setJour] = useState(initial.jour)
  const [rendu, setRendu] = useState<Rendu>(initial.rendu)
  const [pdv, setPdv] = useState<string | null>(initial.pdv)

  const { de, a } = bornes(vue, jour)
  const annee = anneeDe(moisDe(jour))

  // L'adresse suit la lecture : recharger ou partager le lien retombe sur la
  // même vue. L'entrée d'historique est REMPLACÉE — sans quoi chaque
  // changement de jour empilerait un retour arrière inutile — et sans
  // `redirect`, qui empêchait l'arrivée de remonter en haut de la page
  // (`adresse-ecran.ts`).
  useEffect(() => {
    const q = new URLSearchParams({ vue, date: jour })
    if (rendu !== 'synthese') q.set('rendu', rendu)
    if (pdv !== null) q.set('pdv', pdv)
    remplacerRecherche('/comptabilite/chiffre-affaires', q)
  }, [vue, jour, rendu, pdv])

  // Changer de nuit ou de vue referme le point de vente — mais pas au
  // montage : celui que l'adresse vient de rouvrir doit rester ouvert. On
  // compare aux valeurs précédentes plutôt que de sauter « le premier
  // passage », que le double montage du mode strict rejouerait.
  const periodeVue = useRef({ jour, vue })
  useEffect(() => {
    if (periodeVue.current.jour === jour && periodeVue.current.vue === vue) return
    periodeVue.current = { jour, vue }
    setPdv(null)
  }, [jour, vue])

  const nuits = useRessource(
    vue === 'saison' ? null : `/api/v1/finance/revenue/nights?from=${de}&to=${a}`,
    lirePeriodeNuits,
  )
  const annuel = useRessource(`/api/v1/finance/revenue/annual?year=${annee}`, lireAnnuel)
  const donneesAnnuelles = annuel.etat.phase === 'ok' ? annuel.etat.data : null

  // L'exercice qui CONTIENT la date consultée, mois compris — lu pour la vue
  // Saison ET pour la carte « Cumul de l'exercice », qui disent donc le même
  // exercice. La date de lecture ne suit le jour consulté que lorsqu'il sort
  // de l'exercice déjà lu : passer d'une nuit à la suivante ne relit rien.
  const [dateExercice, setDateExercice] = useState(jour)
  const exercice = useRessource(`/api/v1/finance/revenue/season?date=${dateExercice}`, lireSaison)
  const donneesExercice = exercice.etat.phase === 'ok' ? exercice.etat.data : null
  const bornesLues = donneesExercice?.saison ?? null
  useEffect(() => {
    if (bornesLues !== null && jour >= bornesLues.debut && jour <= bornesLues.fin) return
    setDateExercice(jour)
  }, [jour, bornesLues])
  // Tant que l'exercice lu n'est pas celui du jour consulté (relecture en
  // cours), il n'est pas montré : afficher l'ancien sous la nouvelle date
  // serait exactement le défaut corrigé ici.
  const exerciceDuJour = donneesExercice !== null && (bornesLues === null || (jour >= bornesLues.debut && jour <= bornesLues.fin))
    ? donneesExercice
    : null

  // Deux familles, deux listes : les nuits CLÔTURÉES (`lignes`) et les nuits
  // VALIDÉES SANS CLÔTURE DÉTAILLÉE (`validees`). Elles ne se mélangent pas —
  // ni dans une table, ni dans un total, ni dans les colonnes de la matrice.
  const lignes = useMemo(() => (nuits.etat.phase === 'ok' ? nuits.etat.data.nuits : []), [nuits.etat])
  const validees = useMemo(() => (nuits.etat.phase === 'ok' ? nuits.etat.data.validees : []), [nuits.etat])
  const colonnesNuits = useMemo(() => colonnesActives(lignes), [lignes])

  const saison = exerciceDuJour?.saison ?? null
  const moisSaison = useMemo(() => exerciceDuJour?.mois ?? [], [exerciceDuJour])
  const colonnesMois = useMemo(() => colonnesActives(moisSaison), [moisSaison])
  const nuitsSaison = saison === null ? null : nuitsTexte(saison.nuits, saison.nuitsValidees)

  const moisCourant = donneesAnnuelles?.mois.find((m) => m.mois === moisDe(jour)) ?? null
  const refMoisN1 = donneesAnnuelles?.comparaison?.mois.find((m) => m.mois.slice(5) === moisDe(jour).slice(5)) ?? null
  // En vue Jour, la clôture prime : le serveur ne sert jamais une nuit à la
  // fois clôturée et validée (une clôture finalisée remplace la validation),
  // mais si cela arrivait, c'est la clôture qui fait foi.
  const nuitOuverte = vue === 'jour' ? (lignes.find((n) => n.date === jour) ?? null) : null
  const valideeOuverte = vue === 'jour' && nuitOuverte === null
    ? (validees.find((v) => v.date === jour) ?? null)
    : null

  const ouvrirNuit = (date: string) => { setJour(date); setVue('jour') }
  const ouvrirMois = (ym: string) => { setJour(`${ym}-15`); setVue('mois') }

  return (
    <main className="lc-page lc-page--large lc-stagger">
      <SmartBack />

      <header className="fr-hero">
        <div>
          <h1 className="lc-title"><TrendingUp size={26} aria-hidden="true" /> Chiffre d’affaires</h1>
          <p className="lc-subtitle">
            Ce que les clôtures finalisées ont produit, nuit après nuit — et, nommées à
            part, les nuits validées sans clôture détaillée. La billetterie seule n’est
            jamais du chiffre d’affaires : c’est une valeur faciale.
          </p>
        </div>
        <div className="fr-hero__actions">
          <Link className="lc-btn" href="/comptabilite/chiffre-affaires/annuel">
            <CalendarRange size={15} aria-hidden="true" /> CA annuel
          </Link>
        </div>
      </header>

      <div className="fr-toolbar">
        <SegmentedControl value={vue} options={VUES} onChange={setVue} ariaLabel="Profondeur de lecture" />
        <div className="fr-toolbar__group">
          <div className="fr-step">
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Période précédente"
              onClick={() => setJour(pas(vue, jour, -1))}><ChevronLeft size={16} /></button>
            <span className="fr-step__label">{libellePeriode(vue, jour, exerciceDuJour)}</span>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Période suivante"
              onClick={() => setJour(pas(vue, jour, 1))}><ChevronRight size={16} /></button>
          </div>
          {vue !== 'saison' ? (
            <DatePicker
              value={jour} onPick={setJour} label="Choisir une nuit"
              highlightFrom={de} highlightTo={a} ariaLabel="Choisir une nuit commerciale"
            />
          ) : null}
        </div>
        <div className="fr-toolbar__spacer" />
        {vue !== 'jour' ? (
          <SegmentedControl
            value={rendu} size="sm" ariaLabel="Forme du tableau"
            options={[{ value: 'synthese', label: 'Synthèse' }, { value: 'matrice', label: 'Matrice' }]}
            onChange={setRendu}
          />
        ) : null}
      </div>

      <nav className="fr-drill" aria-label="Profondeur">
        <button type="button" className={`fr-drill__step${vue === 'saison' ? ' fr-drill__step--on' : ''}`}
          onClick={() => setVue('saison')}>Saison</button>
        <span aria-hidden="true">›</span>
        <button type="button" className={`fr-drill__step${vue === 'mois' ? ' fr-drill__step--on' : ''}`}
          onClick={() => setVue('mois')}>{libelleMoisCourt(moisDe(jour))} {anneeDe(moisDe(jour))}</button>
        <span aria-hidden="true">›</span>
        <button type="button" className={`fr-drill__step${vue === 'jour' || vue === 'semaine' ? ' fr-drill__step--on' : ''}`}
          onClick={() => setVue('jour')}>{libelleJour(jour)}</button>
        {pdv !== null && nuitOuverte !== null ? (
          <>
            <span aria-hidden="true">›</span>
            <span className="fr-drill__step fr-drill__step--on">
              {nuitOuverte.pointsDeVente.find((p) => p.id === pdv)?.nom ?? 'Point de vente'}
            </span>
          </>
        ) : null}
      </nav>

      {/* ── Cumuls : LUS, jamais recalculés depuis les nuits affichées ── */}
      <div className="fr-kpis">
        <div className="fr-kpi">
          <span className="fi-kpi__label">Cumul du mois</span>
          <span className="fi-kpi__value">{formatMoney(moisCourant?.retenu ?? null)}</span>
          <span className="fr-kpi__foot">
            {libelleMois(moisDe(jour))}
            {moisCourant !== null && (moisCourant.nuitsValidees ?? 0) > 0
              ? <> · dont {formatMoney(moisCourant.valide)} validés sans clôture détaillée</>
              : null}
            {refMoisN1 !== null
              ? <> · <Comparaison actuel={moisCourant?.retenu ?? null} reference={refMoisN1.retenu} quoi="vs. l’an dernier" /></>
              : null}
          </span>
        </div>
        <div className="fr-kpi fr-kpi--accent">
          <span className="fi-kpi__label">Cumul de l’exercice</span>
          <span className="fi-kpi__value">
            {exerciceDuJour !== null && saison === null
              ? <span className="fr-mini">Saison à configurer</span>
              : formatMoney(saison?.totaux.retenu ?? null)}
          </span>
          <span className="fr-kpi__foot">
            {exerciceDuJour === null
              ? 'Exercice non lu.'
              : saison === null
                ? <>L’exercice comptable n’est pas défini — <Link className="lc-link" href="/comptabilite/regles">le configurer</Link>.</>
                : <>Du {libelleJour(saison.debut)} au {libelleJour(saison.fin)}
                  {nuitsSaison !== null ? ` · ${nuitsSaison}` : ''}</>}
          </span>
        </div>
        <div className="fr-kpi">
          <span className="fi-kpi__label">Année civile {annee}</span>
          <span className="fi-kpi__value">{formatMoney(donneesAnnuelles?.totaux.retenu ?? null)}</span>
          <span className="fr-kpi__foot">
            {donneesAnnuelles?.comparaison
              ? <Comparaison actuel={donneesAnnuelles.comparaison.actuel} reference={donneesAnnuelles.comparaison.precedent} quoi={`vs. ${donneesAnnuelles.comparaison.anneePrecedente ?? 'l’an dernier'}`} />
              : 'Aucun historique comparable : la comparaison est masquée plutôt qu’affichée à zéro.'}
          </span>
        </div>
      </div>

      {/* ── Le contenu de la vue ── */}
      {vue === 'saison' ? (
        <section className="fr-section">
          <EtatBloc etat={exercice.etat} quoi="l’exercice" />
          {exerciceDuJour !== null ? (
            saison === null ? (
              <div className="fr-state">
                <span className="fr-state__title">Saison à configurer</span>
                <p className="fr-state__text">
                  L’exercice comptable n’est renseigné nulle part, et Gestion ne le devine
                  pas : un début de saison inventé produirait des cumuls qui auraient l’air
                  justes. Indiquez le jour et le mois d’ouverture, et cette vue s’affichera.
                </p>
                <Link className="lc-btn lc-btn--primary" href="/comptabilite/regles">Configurer l’exercice</Link>
              </div>
            ) : (
              <>
                <TroisLignes
                  calcule={saison.totaux.calcule} valide={saison.totaux.valide} nuitsValidees={saison.nuitsValidees}
                  ajustements={saison.totaux.ajustements} retenu={saison.totaux.retenu}
                  note={`Exercice du ${libelleJour(saison.debut)} au ${libelleJour(saison.fin)}. Ce total vient du serveur : ce n’est pas la somme des lignes affichées.`}
                />
                {saison.totaux.composantes.length > 0 ? (
                  <div className="fi-tablewrap fr-compact">
                    <table className="fi-table">
                      <thead><tr><th scope="col">Composante de l’exercice</th><th scope="col" className="fi-money">Montant</th></tr></thead>
                      <tbody>
                        {saison.totaux.composantes.map((c) => (
                          <tr key={c.code}>
                            <td className="fr-cell-main">{c.libelle}</td>
                            <td className="fi-money">{formatMoney(c.cents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {moisSaison.length === 0 ? (
                  <div className="fi-empty">
                    <div className="fi-empty__title">Aucun mois alimenté sur cet exercice</div>
                    Aucune clôture finalisée n’a produit de recette entre le
                    {' '}{libelleJour(saison.debut)} et le {libelleJour(saison.fin)}, et aucune
                    nuit n’y a été validée sans clôture détaillée.
                  </div>
                ) : (
                  <>
                    <TableMois
                      mois={moisSaison} colonnes={colonnesMois} rendu={rendu}
                      precedent={[]} onOuvrir={ouvrirMois}
                    />
                    {saison.debut.slice(8) !== '01' ? (
                      <p className="fr-mini">
                        L’exercice ne commence pas un 1ᵉʳ : les mois listés sont des mois
                        civils entiers, ceux des deux bornes débordent donc légèrement de
                        l’exercice. Le total ci-dessus, lui, est celui de l’exercice exact.
                      </p>
                    ) : null}
                  </>
                )}
              </>
            )
          ) : null}
        </section>
      ) : (
        <section className="fr-section">
          <div className="fr-stack">
            {/* Le mois, tel que le serveur l'arrête : l'addition se refait ici,
                au-dessus des nuits — qui ne la refont pas (pas d'ajustements). */}
            {vue === 'mois' && moisCourant !== null ? (
              <TroisLignes
                calcule={moisCourant.calcule} valide={moisCourant.valide} nuitsValidees={moisCourant.nuitsValidees}
                ajustements={moisCourant.ajustements} retenu={moisCourant.retenu}
                note={`${libelleMois(moisCourant.mois)}, tel que le serveur l’arrête. Ce n’est pas la somme des nuits affichées ci-dessous : un ajustement porte sur le mois, jamais sur une nuit.`}
              />
            ) : null}
            <EtatBloc etat={nuits.etat} quoi="les nuits de la période" />
            {nuits.etat.phase === 'ok' ? (
              lignes.length === 0 && validees.length === 0 ? (
                <div className="fi-empty">
                  <div className="fi-empty__title">Aucune recette sur cette période</div>
                  Aucune clôture finalisée n’a produit de composante entre le {libelleJour(de)} et
                  le {libelleJour(a)}, et aucune nuit n’y a été validée sans clôture détaillée.
                  Ce n’est pas un zéro : c’est une absence de donnée.
                </div>
              ) : vue === 'jour' && nuitOuverte !== null ? (
                <VueNuit nuit={nuitOuverte} pdv={pdv} onPdv={setPdv} />
              ) : vue === 'jour' && valideeOuverte !== null ? (
                <VueNuitValidee nuit={valideeOuverte} />
              ) : (
                <>
                  {lignes.length === 0 ? (
                    <p className="fr-mini">
                      Aucune nuit clôturée sur cette période : seules des nuits validées sans
                      clôture détaillée la composent.
                    </p>
                  ) : rendu === 'matrice' ? (
                    <TableMatriceNuits nuits={lignes} colonnes={colonnesNuits} onOuvrir={ouvrirNuit} />
                  ) : (
                    <TableNuitsSynthese nuits={lignes} onOuvrir={ouvrirNuit} />
                  )}
                  {validees.length > 0 ? <TableNuitsValidees validees={validees} onOuvrir={ouvrirNuit} /> : null}
                  {lignes.length > 0 && validees.length > 0 ? (
                    <TotalNuitsAffichees
                      clotures={totalStrict(lignes, (n) => n.retenu)}
                      validees={totalStrict(validees, (v) => v.cents)}
                    />
                  ) : null}
                </>
              )
            ) : null}
          </div>
        </section>
      )}
    </main>
  )
}
